import {
  type AppLocale,
  getActiveLocale,
  LANGUAGE_REGISTRY,
  useActiveLocale,
} from "./language-lens";
import {
  getNumberFormattingLocale,
  useNumberFormattingLocale,
} from "./number-format-lens";

export type LanguageDateTimeValue = Date | number | string;
export type LanguageNumberValue = bigint | number;
export type LanguageNumberFormatOptions = Omit<
  Intl.NumberFormatOptions,
  "currency" | "currencyDisplay" | "currencySign" | "style"
>;
export type LanguagePercentFormatOptions = LanguageNumberFormatOptions;

export const DEFAULT_LANGUAGE_DATE_TIME_FORMAT_OPTIONS = {
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  month: "numeric",
  second: "numeric",
  year: "numeric",
} as const satisfies Intl.DateTimeFormatOptions;

const DATE_TIME_FORMATTERS = new Map<string, Intl.DateTimeFormat>();
const NUMBER_FORMATTERS = new Map<string, Intl.NumberFormat>();

type IntlFormatOptions =
  | Intl.DateTimeFormatOptions
  | Intl.NumberFormatOptions;

function getFormattingLocaleForLanguage(locale: AppLocale): string {
  return LANGUAGE_REGISTRY[locale].formattingLocale;
}

function toOptionsHash(options: IntlFormatOptions): string {
  return JSON.stringify(
    Object.entries(options)
      .filter(([, value]) => value !== undefined)
      .sort(([left], [right]) => left.localeCompare(right)),
  );
}

function toFormatterKey(
  formattingLocale: string,
  options: IntlFormatOptions,
): string {
  return `${formattingLocale}:${toOptionsHash(options)}`;
}

function getDateTimeFormatterForLocale(
  formattingLocale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = toFormatterKey(formattingLocale, options);
  const cached = DATE_TIME_FORMATTERS.get(key);
  if (cached !== undefined) {
    return cached;
  }

  const formatter = new Intl.DateTimeFormat(formattingLocale, options);
  DATE_TIME_FORMATTERS.set(key, formatter);
  return formatter;
}

function getNumberFormatterForLocale(
  formattingLocale: string,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const key = toFormatterKey(formattingLocale, options);
  const cached = NUMBER_FORMATTERS.get(key);
  if (cached !== undefined) {
    return cached;
  }

  const formatter = new Intl.NumberFormat(formattingLocale, options);
  NUMBER_FORMATTERS.set(key, formatter);
  return formatter;
}

function toDateTimeValue(value: LanguageDateTimeValue): Date | number {
  return typeof value === "string" ? new Date(value) : value;
}

export function getLanguageFormattingLocale(): string {
  return getFormattingLocaleForLanguage(getActiveLocale());
}

export function getIntlDateTimeFormat(
  options: Intl.DateTimeFormatOptions = {},
): Intl.DateTimeFormat {
  return getDateTimeFormatterForLocale(getLanguageFormattingLocale(), options);
}

export function getIntlNumberFormat(
  options: Intl.NumberFormatOptions = {},
): Intl.NumberFormat {
  return getNumberFormatterForLocale(getNumberFormattingLocale(), options);
}

export function formatLanguageDate(
  value: LanguageDateTimeValue,
  options: Intl.DateTimeFormatOptions = {},
): string {
  return getIntlDateTimeFormat(options).format(toDateTimeValue(value));
}

export function formatLanguageTime(
  value: LanguageDateTimeValue,
  options: Intl.DateTimeFormatOptions = {},
): string {
  return getIntlDateTimeFormat(options).format(toDateTimeValue(value));
}

export function formatLanguageNumber(
  value: LanguageNumberValue,
  options: LanguageNumberFormatOptions = {},
): string {
  return getIntlNumberFormat(options).format(value);
}

export function formatLanguagePercent(
  value: LanguageNumberValue,
  options: LanguagePercentFormatOptions = {},
): string {
  return getIntlNumberFormat({
    ...options,
    style: "percent",
  }).format(value);
}

export function useLanguageFormattingLocale(): string {
  return getFormattingLocaleForLanguage(useActiveLocale());
}

export function useLanguageDateFormatter(
  options: Intl.DateTimeFormatOptions = {},
): Intl.DateTimeFormat {
  return getDateTimeFormatterForLocale(
    useLanguageFormattingLocale(),
    options,
  );
}

export function useLanguageTimeFormatter(
  options: Intl.DateTimeFormatOptions = {},
): Intl.DateTimeFormat {
  return getDateTimeFormatterForLocale(
    useLanguageFormattingLocale(),
    options,
  );
}

export function useLanguageNumberFormatter(
  options: LanguageNumberFormatOptions = {},
): Intl.NumberFormat {
  return getNumberFormatterForLocale(useNumberFormattingLocale(), options);
}

export function useLanguagePercentFormatter(
  options: LanguagePercentFormatOptions = {},
): Intl.NumberFormat {
  return getNumberFormatterForLocale(useNumberFormattingLocale(), {
    ...options,
    style: "percent",
  });
}
